import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useUsageMonitoringControllerGetUsageQuery } from '@shared/services/apiService';

const useStyles = makeStyles()((theme) => ({
  tableContainer: {
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.shadows[3],
    marginBottom: theme.spacing(4),
    overflow: 'hidden',
  },
  tableHeadCell: {
    fontWeight: 'bold',
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  tableCell: {
    color: theme.palette.text.secondary,
  },
}));

export const MonitoringTokensPage: React.FC = () => {
  const { classes } = useStyles();
  // TODO: core API doesn't support this kind of usage yet
  const { data, error, refetch, isLoading } = useUsageMonitoringControllerGetUsageQuery();

  useEffect(() => {
    if (error && 'status' in error && error.status === 401) {
      refetch();
    }
  }, [error]);

  if (isLoading && !data) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography variant="h6">No data available</Typography>
      </Box>
    );
  }

  const { providers } = data;

  const isPrimitive = (val: any) =>
    val === null || ['string', 'number', 'boolean'].includes(typeof val);

  const formatKey = (key: string) =>
    key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^./, (c) => c.toUpperCase());

  const renderKeyValueTable = (obj: Record<string, any>) => (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableHeadCell}>Key</TableCell>
            <TableCell className={classes.tableHeadCell}>Value</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.keys(obj || {}).map((key) => {
            const value = (obj as any)[key];
            return (
              <TableRow key={key} className={classes.tableRow}>
                <TableCell className={classes.tableCell}>{formatKey(key)}</TableCell>
                <TableCell className={classes.tableCell}>
                  {isPrimitive(value)
                    ? typeof value === 'number'
                      ? value.toLocaleString()
                      : String(value)
                    : Array.isArray(value)
                      ? JSON.stringify(value, null, 2)
                      : JSON.stringify(value, null, 2)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const getColumnsFromArrayOfObjects = (rows: any[]) => {
    const set = new Set<string>();
    rows.forEach((r) => {
      if (r && typeof r === 'object' && !Array.isArray(r)) {
        Object.keys(r).forEach((k) => set.add(k));
      }
    });
    return Array.from(set);
  };

  const renderArray = (arr: any[]) => {
    if (!arr || arr.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          No data
        </Typography>
      );
    }

    const isArrayOfObjects = arr.every((el) => el && typeof el === 'object' && !Array.isArray(el));

    if (isArrayOfObjects) {
      const columns = getColumnsFromArrayOfObjects(arr);
      return (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((key) => (
                  <TableCell key={key} className={classes.tableHeadCell}>
                    {formatKey(key)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {arr.map((row, idx) => (
                <TableRow key={idx} className={classes.tableRow}>
                  {columns.map((key) => {
                    const value = row?.[key];
                    return (
                      <TableCell key={key} className={classes.tableCell}>
                        {isPrimitive(value)
                          ? typeof value === 'number'
                            ? value.toLocaleString()
                            : String(value)
                          : JSON.stringify(value, null, 2)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    // Array of primitives or mixed types -> single column table
    return (
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeadCell}>Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {arr.map((value, idx) => (
              <TableRow key={idx} className={classes.tableRow}>
                <TableCell className={classes.tableCell}>
                  {isPrimitive(value) ? String(value) : JSON.stringify(value, null, 2)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderResult = (result: any) => {
    if (result == null) {
      return (
        <Typography variant="body2" color="textSecondary">
          No result
        </Typography>
      );
    }

    if (Array.isArray(result)) {
      return renderArray(result);
    }

    if (typeof result === 'object') {
      return renderKeyValueTable(result);
    }

    return <Typography variant="body1">{String(result)}</Typography>;
  };

  return (
    <Box>
      {Array.isArray(providers) && providers.length > 0 ? (
        providers.map((p, idx) => (
          <Box key={`${p.provider}-${idx}`} sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              {p.provider}
            </Typography>
            {p.period && (
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Period: {p.period.start} to {p.period.end}
              </Typography>
            )}
            {renderResult((p as any).result)}
          </Box>
        ))
      ) : (
        <Typography variant="h6">No providers data</Typography>
      )}
    </Box>
  );
};

export default MonitoringTokensPage;
